import { useEffect, useRef, useCallback } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { toast } from 'sonner'

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove']
const WARNING_BEFORE_MS = 60_000 // warn 60s before logout
const THROTTLE_MS = 30_000 // only reset timer every 30s max

export function useIdleTimeout() {
  const { user, logout } = useAuth()
  const timerRef = useRef<number>()
  const warningRef = useRef<number>()
  const lastResetRef = useRef<number>(Date.now())

  const enabled = user?.session_timeout_enabled && user?.session_duration_minutes
  const timeoutMs = (user?.session_duration_minutes ?? 1440) * 60_000

  const resetTimer = useCallback(() => {
    const now = Date.now()
    if (now - lastResetRef.current < THROTTLE_MS) return
    lastResetRef.current = now

    if (timerRef.current) clearTimeout(timerRef.current)
    if (warningRef.current) clearTimeout(warningRef.current)

    // Set warning timer
    if (timeoutMs > WARNING_BEFORE_MS) {
      warningRef.current = window.setTimeout(() => {
        toast.warning('Your session will expire in 1 minute due to inactivity')
      }, timeoutMs - WARNING_BEFORE_MS)
    }

    // Set logout timer
    timerRef.current = window.setTimeout(() => {
      toast.error('Session expired due to inactivity')
      logout()
    }, timeoutMs)
  }, [timeoutMs, logout])

  useEffect(() => {
    if (!enabled) return

    // Initial timer
    lastResetRef.current = 0 // ensure first call is not throttled
    resetTimer()

    const handler = () => resetTimer()
    ACTIVITY_EVENTS.forEach(e => document.addEventListener(e, handler, { passive: true }))

    return () => {
      ACTIVITY_EVENTS.forEach(e => document.removeEventListener(e, handler))
      if (timerRef.current) clearTimeout(timerRef.current)
      if (warningRef.current) clearTimeout(warningRef.current)
    }
  }, [enabled, resetTimer])
}
